import { useState } from 'react'
import { History, Search } from 'lucide-react'
import {
  DateValue, EmptyState, ErrorState, FilterBar, PageHeader,
} from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { useActivity, useAuditedTables, useRevisions } from '../api'
import type { AuditEntryView } from '../types'

/**
 * Two records of the same past, answering different questions.
 *
 * <p>The activity log is the narrative — "Ayesha approved a leave request" — written
 * deliberately by the modules, and it is what somebody searches. The Envers revisions are
 * every column that changed, written by the persistence layer whether anybody remembered to
 * log it or not, and they are what somebody needs once they have found the row.
 */
export function AuditLogPage() {
  useBreadcrumb()
  const [type, setType] = useState('')
  const [table, setTable] = useState('')
  const [entityId, setEntityId] = useState('')
  const [lookup, setLookup] = useState<{ table: string; entityId: string }>({ table: '', entityId: '' })

  const activity = useActivity({ type })
  const tables = useAuditedTables()
  const revisions = useRevisions(lookup.table, lookup.entityId)

  const types = [...new Set((activity.data ?? []).map((entry) => entry.action))].sort()

  return (
    <div className="space-y-4">
      <PageHeader
        title="Audit log"
        subtitle="Who did what, and what each column held before they did."
      />

      <Card className="p-3">
        <p className="text-sm font-medium text-[var(--color-heading)]">A row's history</p>
        <p className="mt-1 text-xs text-[var(--color-muted)]">
          Pick the table and paste the row's id. Revisions are held per row, so there is no
          "everything that ever changed" list to page through — and a table that Envers does
          not mirror is not offered.
        </p>
        <form
          className="mt-3 flex flex-wrap items-end gap-2"
          onSubmit={(event) => { event.preventDefault(); setLookup({ table, entityId: entityId.trim() }) }}
        >
          <Select aria-label="Table" value={table} onChange={(event) => setTable(event.target.value)}>
            <option value="">Choose a table</option>
            {tables.data?.map((name) => (
              <option key={name} value={name}>{name.replace(/_aud$/, '')}</option>
            ))}
          </Select>
          <Input
            aria-label="Row id"
            className="max-w-[22rem]"
            placeholder="00000000-0000-0000-0000-000000000000"
            value={entityId}
            onChange={(event) => setEntityId(event.target.value)}
          />
          <Button type="submit" disabled={!table || entityId.trim().length === 0}>
            <Search className="size-4" aria-hidden="true" />
            Show revisions
          </Button>
        </form>

        {revisions.isPending && lookup.entityId && <Skeleton className="mt-3 h-20" />}
        {revisions.isError && (
          <div className="mt-3"><ErrorState error={revisions.error} onRetry={() => void revisions.refetch()} /></div>
        )}
        {revisions.isSuccess && revisions.data.length === 0 && (
          <p className="mt-3 rounded-md border border-[var(--color-border)] bg-[var(--color-page)] p-3 text-sm text-[var(--color-body)]">
            No revision for that row. Either the id belongs to a different table, or the row has
            not been changed since auditing began.
          </p>
        )}
        {revisions.isSuccess && revisions.data.length > 0 && (
          <ol className="mt-3 space-y-2">
            {revisions.data.map((entry) => (
              <li key={`${entry.tableName}-${entry.revision}`} className="rounded-md border border-[var(--color-border)] p-3">
                <Entry entry={entry} />
              </li>
            ))}
          </ol>
        )}
      </Card>

      <FilterBar>
        <Select aria-label="Kind" value={type} onChange={(event) => setType(event.target.value)}>
          <option value="">Every kind</option>
          {types.map((name) => <option key={name} value={name}>{name}</option>)}
        </Select>
      </FilterBar>

      <Card>
        {activity.isPending && <Skeleton className="m-4 h-24" />}
        {activity.isError && (
          <ErrorState error={activity.error} onRetry={() => void activity.refetch()} />
        )}
        {activity.isSuccess && activity.data.length === 0 && (
          <EmptyState
            title="Nothing recorded yet"
            description="The narrative fills as people work. A row's column history is above and does not depend on it."
          />
        )}
        {activity.isSuccess && activity.data.length > 0 && (
          <ul className="divide-y divide-[var(--color-border)]">
            {activity.data.map((entry) => (
              <li key={`${entry.entityId ?? 'x'}-${entry.at ?? ''}-${entry.summary}`} className="p-3">
                <Entry entry={entry} />
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  )
}

function Entry({ entry }: { entry: AuditEntryView }) {
  return (
    <div>
      <p className="flex flex-wrap items-center gap-2 text-sm text-[var(--color-heading)]">
        <History className="size-4 text-[var(--color-muted)]" aria-hidden="true" />
        <span className="font-medium">{entry.action}</span>
        {entry.tableName && <span className="font-mono text-xs">{entry.tableName}</span>}
        {entry.revision != null && (
          <span className="text-xs text-[var(--color-muted)]">revision {entry.revision}</span>
        )}
      </p>
      <p className="mt-1 text-sm text-[var(--color-body)]">{entry.summary}</p>
      <p className="mt-1 text-xs text-[var(--color-muted)]">
        {entry.actorName ?? 'No signed-in user — a job or a system task'}
        {entry.at ? <> · <DateValue value={entry.at} withTime /></> : ' · no time recorded'}
      </p>
      {entry.changes.length > 0 && (
        <div className="mt-2 overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-[var(--color-muted)]">
              <tr>
                <th className="py-1 text-start font-medium">Column</th>
                <th className="py-1 text-start font-medium">Before</th>
                <th className="py-1 text-start font-medium">After</th>
              </tr>
            </thead>
            <tbody>
              {entry.changes.map((change) => (
                <tr key={change.field}>
                  <td className="py-1 pe-3 font-mono">{change.field}</td>
                  <td className="py-1 pe-3 text-[var(--color-muted)]">{change.before ?? '—'}</td>
                  <td className="py-1">{change.after ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
